/**
 * PricingHeader - Pricing box card for payment page
 *
 * Shows trial pricing in a card format with:
 * - "7-day free trial" heading
 * - "then $X/month" in salmon/coral
 * - "*billed annually at $X" footnote with optional strikethrough for discounts
 * - Optional coupon badge with dismiss button
 */

interface PricingHeaderProps {
  /** Whether this is a trial offer */
  isTrial?: boolean;
  /** Annual price in dollars (regular, before discount) */
  annualPrice?: number;
  /** Whether pricing is loading */
  isLoading?: boolean;
  /** Applied coupon code (e.g. "HEALTH30") */
  couponCode?: string;
  /** Discounted annual price when coupon is applied */
  discountedAnnualPrice?: number;
  /** Callback when coupon badge dismiss is clicked */
  onRemoveCoupon?: () => void;
}

export function PricingHeader({
  isTrial = true,
  annualPrice,
  isLoading = false,
  couponCode,
  discountedAnnualPrice,
  onRemoveCoupon,
}: PricingHeaderProps) {
  const hasDiscount =
    discountedAnnualPrice !== undefined &&
    annualPrice !== undefined &&
    discountedAnnualPrice < annualPrice;

  const discountPercent =
    hasDiscount ? Math.round(((annualPrice - discountedAnnualPrice) / annualPrice) * 100) : 0;

  if (isLoading || annualPrice === undefined) {
    return (
      <div className="relative w-full p-2">
        <div className="flex items-center justify-between">
          <div className="h-5 w-32 animate-pulse rounded bg-slate-200" />
          <div className="h-5 w-20 animate-pulse rounded bg-slate-200" />
        </div>
        <div className="mt-2 h-4 w-48 animate-pulse rounded bg-slate-200" />
      </div>
    );
  }

  if (isTrial) {
    return (
      <div className="relative w-full rounded-2xl bg-white px-6 pt-5 pb-5">
        {/* Top row: trial label + price + $0 today */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xl font-bold text-catch-dark-blue-90">
              7-day free trial
            </span>
            <p className="mt-1 text-base font-medium text-catch-dark-blue-90">
              then ${Math.round((hasDiscount ? discountedAnnualPrice : annualPrice) / 12)}/month
            </p>
            <p className="mt-0.5 text-sm text-gray-500">
              {hasDiscount ? (
                <>*billed annually at <span className="line-through">${annualPrice}</span> ${discountedAnnualPrice}</>
              ) : (
                <>*billed annually at ${annualPrice}</>
              )}
            </p>

            {/* Coupon badge + HSA/FSA badge */}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {couponCode && hasDiscount && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-green-300 bg-green-50 py-1 pr-2.5 pl-[7px] text-xs font-semibold text-green-600">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6" fill="#22C55E" opacity="0.15" />
                    <path d="M4.5 7L6 8.5L9.5 5" stroke="#16A34A" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {couponCode.toUpperCase()}
                  <span className="text-green-400">·</span>
                  <span className="text-[11px] text-green-700">{discountPercent}% off</span>
                  {onRemoveCoupon && (
                    <button
                      type="button"
                      onClick={onRemoveCoupon}
                      className="ml-0.5 flex items-center"
                      aria-label="Remove coupon"
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M3 3L9 9M9 3L3 9" stroke="#86EFAC" strokeWidth="1.3" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}
                </span>
              )}
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[#F26B4A]"
                style={{ background: 'rgba(242, 107, 74, 0.1)' }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7 1L2.5 3V6.5C2.5 9.5 4.5 12 7 13C9.5 12 11.5 9.5 11.5 6.5V3L7 1Z" stroke="currentColor" strokeWidth="1.2" fill="currentColor" fillOpacity="0.12" />
                  <path d="M4.8 7.2L6.2 8.6L9.2 5.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-xs font-semibold">HSA/FSA Eligible</span>
                <span className="-ml-px">&sup1;</span>
              </span>
            </div>
          </div>
          <div
            className="rounded-lg px-3 py-1.5 text-center"
            style={{ background: 'rgba(242, 107, 74, 0.1)' }}
          >
            <div className="text-2xl font-extrabold text-[#F26B4A]">$0</div>
            <div className="text-[11px] font-medium text-[#F26B4A]">today</div>
          </div>
        </div>
      </div>
    );
  }

  // Non-trial pricing (direct purchase) — annual-only display
  const effectiveAnnualPrice = hasDiscount ? discountedAnnualPrice : annualPrice;

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Pricing card */}
      <div className="relative w-full rounded-2xl bg-white px-5 pt-5 pb-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 flex-col">
            {/* Inline overflow-wrap:normal beats the global unlayered
                `:where(*){overflow-wrap:break-word}` reset, which otherwise
                breaks "Membership" mid-word once the column shrinks. */}
            <span
              className="text-xl font-bold text-catch-dark-blue-90 sm:text-2xl"
              style={{ overflowWrap: 'normal', wordBreak: 'normal' }}
            >
              Catch Membership
            </span>

            {/* HSA/FSA badge only — discount is conveyed visually by the price box (and pill lives on the sticky CTA). */}
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[#F26B4A]"
                style={{ background: 'rgba(242, 107, 74, 0.1)' }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7 1L2.5 3V6.5C2.5 9.5 4.5 12 7 13C9.5 12 11.5 9.5 11.5 6.5V3L7 1Z" stroke="currentColor" strokeWidth="1.2" fill="currentColor" fillOpacity="0.12" />
                  <path d="M4.8 7.2L6.2 8.6L9.2 5.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-xs font-semibold">HSA/FSA Eligible</span>
                <span className="-ml-px">&sup1;</span>
              </span>
            </div>
          </div>

          {/* Annual price box — coral when no coupon, mint + stacked when coupon applied */}
          {hasDiscount ? (
            <div
              className="flex flex-col items-center px-3 py-2 shrink-0 sm:px-4"
              style={{ backgroundColor: 'rgba(124,241,199,0.35)', borderRadius: '12px' }}
            >
              <div className="flex items-baseline">
                <span
                  className="text-2xl font-extrabold leading-none sm:text-[26px]"
                  style={{ color: '#0a8a5f' }}
                >
                  ${effectiveAnnualPrice}
                </span>
                <span className="ml-0.5 text-xs" style={{ color: '#0a8a5f' }}>
                  /yr
                </span>
              </div>
              <span
                className="mt-0.5 text-xs line-through"
                style={{ color: '#0a8a5f', opacity: 0.6 }}
              >
                ${annualPrice}/yr
              </span>
            </div>
          ) : (
            <div
              className="flex items-baseline justify-center px-3 py-3 shrink-0 sm:px-4"
              style={{ backgroundColor: 'rgba(242,107,74,0.10)', borderRadius: '12px' }}
            >
              <span
                className="text-2xl font-extrabold leading-none sm:text-[26px]"
                style={{ color: '#F26B4A' }}
              >
                ${effectiveAnnualPrice}
              </span>
              <span className="ml-0.5 text-xs" style={{ color: '#F26B4A' }}>
                /yr
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Cancel anytime + 30-day money back guarantee */}
      <ul className="mt-1 flex flex-col items-start gap-2">
        {['Cancel anytime', '30-day money back guarantee'].map((text) => (
          <li key={text} className="flex items-center gap-2 text-sm text-catch-dark-blue-90">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              className="h-5 w-5 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="12" cy="12" r="10" fill="#7CF1C7" />
              <path
                d="M8 12.5l2.5 2.5L16 9.5"
                stroke="#0F766E"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default PricingHeader;
